import type { BannerColors } from '@sunwebgroup/uds-ui/banner'
import type { PaymentResult } from '@/scopes/payment/domain/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import * as React from 'react'
import { useIntl } from '@/intl'
import styles from './PaymentResultBanner.module.css'

const PAYMENT_RESULT_BANNER_COLOR: Record<PaymentResult, BannerColors> = {
  ok: 'success',
  pending: 'informative',
  decline: 'warning',
  exception: 'danger',
}

const PAYMENT_RESULT_TITLE_KEY: Record<PaymentResult, string> = {
  ok: 'payment_scope_result_ok_title',
  pending: 'payment_scope_result_pending_title',
  decline: 'payment_scope_result_decline_title',
  exception: 'payment_scope_result_exception_title',
}

const PAYMENT_RESULT_DESCRIPTION_KEY: Record<PaymentResult, string> = {
  ok: 'payment_scope_result_ok_description',
  pending: 'payment_scope_result_pending_description',
  decline: 'payment_scope_result_decline_description',
  exception: 'payment_scope_result_exception_description',
}

interface PaymentResultBannerProps {
  paymentResult: PaymentResult | null
  message?: string
  supportingMessage?: string
}

export function PaymentResultBanner({
  paymentResult,
  message,
  supportingMessage,
}: PaymentResultBannerProps) {
  const { formatMessageIfExists } = useIntl()
  const wrapperRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!paymentResult) {
      return
    }

    wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [paymentResult])

  if (!paymentResult) {
    return null
  }

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <Banner
        color={PAYMENT_RESULT_BANNER_COLOR[paymentResult]}
        size="M"
        hasIcon
        contentIsCentered
      >
        <Banner.TextsWrapper>
          <Banner.Text>{message ?? formatMessageIfExists({ id: PAYMENT_RESULT_TITLE_KEY[paymentResult] })}</Banner.Text>
          <Banner.SupportingText>{supportingMessage ?? formatMessageIfExists({ id: PAYMENT_RESULT_DESCRIPTION_KEY[paymentResult] })}</Banner.SupportingText>
        </Banner.TextsWrapper>
      </Banner>
    </div>
  )
}
